import React,{useState,useRef} from 'react';
import {patterns} from './data/patterns';
import PatternSelector from './components/configurator/PatternSelector';
import ZoneEditor from './components/configurator/ZoneEditor';
import FloorPreview from './components/configurator/FloorPreview';
import {Button} from './components/common/Button';
import SafeIcon from './components/common/SafeIcon';
import {FaDownload,FaShoppingCart,FaUndo,FaCube} from 'react-icons/fa';
import * as htmlToImage from 'html-to-image';
import {saveAs} from 'file-saver';
import {motion,AnimatePresence} from 'framer-motion';

function App() {
  const [activePatternId,setActivePatternId]=useState(patterns[0].id);
  const [customColors,setCustomColors]=useState(patterns[0].defaultColors);
  const [activeZone,setActiveZone]=useState(patterns[0].zones[0].id);
  const [isExporting,setIsExporting]=useState(false);

  const activePattern=patterns.find(p=> p.id===activePatternId);
  const PatternComponent=activePattern.component;

  const handlePatternChange=(id)=> {
    const newPattern=patterns.find(p=> p.id===id);
    setActivePatternId(id);
    setCustomColors(newPattern.defaultColors);
    setActiveZone(newPattern.zones[0].id);
  };

  const handleColorChange=(zoneId,colorHex)=> {
    setCustomColors(prev=> ({...prev,[zoneId]: colorHex}));
  };

  const handleReset=()=> {
    setCustomColors(activePattern.defaultColors);
  };

  const handleDownload=async ()=> {
    setIsExporting(true);
    // Note: html-to-image might have difficulty with 3D CSS transforms depending on browser
    // We target the main grid container
    const element=document.getElementById('floor-preview-grid');
    if (element) {
      try {
        const dataUrl=await htmlToImage.toPng(element,{quality: 0.95,backgroundColor: '#fff'});
        saveAs(dataUrl,`tile-design-${activePatternId}.png`);
      } catch (error) {
        console.error('Download failed',error);
      }
    } 
    setIsExporting(false);
  };

  const handleOrder=()=> {
    const orderPayload={
      patternId: activePatternId,
      colors: customColors,
      timestamp: new Date().toISOString(),
      meta: {zones: activePattern.zones.length,palette: 'standard-cement'}
    };
    alert("Order Payload Generated:\n" + JSON.stringify(orderPayload,null,2));
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white transform rotate-45"></div>
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">TileStudio <span className="font-normal text-slate-500">Configurator</span></h1>
          </div>
          <div className="flex items-center gap-3">
             <Button variant="ghost" className="hidden sm:flex" onClick={handleReset}>
                <SafeIcon icon={FaUndo} className="mr-2" /> Reset
             </Button>
             <Button variant="secondary" onClick={handleDownload} disabled={isExporting}>
                <SafeIcon icon={FaDownload} className="mr-2" /> {isExporting ? 'Saving...' : 'Save Design'}
             </Button>
             <Button variant="primary" onClick={handleOrder}>
                <SafeIcon icon={FaShoppingCart} className="mr-2" /> Order Sample
             </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full p-4 md:p-6 lg:p-8 flex flex-col lg:flex-row gap-8">
        
        {/* Left Sidebar: Controls */}
        <div className="w-full lg:w-96 flex flex-col gap-8 shrink-0">
            
            {/* Pattern Selection */}
            <section className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">1. Choose Pattern</h2>
                </div>
                <PatternSelector 
                    currentPatternId={activePatternId} 
                    onSelect={handlePatternChange} 
                />
            </section>

            {/* Color Customization */}
            <section className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 flex-1">
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">2. Customise Colours</h2>
                <ZoneEditor 
                   pattern={activePattern}
                   currentColors={customColors}
                   activeZone={activeZone}
                   onZoneSelect={setActiveZone}
                   onColorChange={handleColorChange}
                />
            </section>

        </div>

        {/* Right Area: Preview */}
        <div className="flex-1 flex flex-col gap-6 min-h-[600px]">
            {/* Main Visualizer */}
            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-1.5 relative group">
                <FloorPreview 
                    PatternComponent={PatternComponent}
                    colors={customColors}
                />
            </div>

            {/* Single Tile Detail */}
            <div className="h-32 bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <div className="w-24 h-24 border border-slate-100 shadow-sm rounded-lg overflow-hidden shrink-0">
                         <PatternComponent colors={customColors} className="w-full h-full" />
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-900">{activePattern.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                            {Object.keys(customColors).length} Active Colors
                        </p>
                        <div className="flex gap-1 mt-2">
                             {Object.values(customColors).map((c, i) => (
                                <div key={i} className="w-4 h-4 rounded-full border border-slate-100" style={{ backgroundColor: c}} />
                             ))}
                        </div>
                    </div>
                </div>
                <div className="text-right hidden sm:block">
                     <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Dimensions</div>
                     <div className="text-lg font-mono text-slate-700">20 x 20 cm</div>
                </div>
            </div>
        </div>

      </main>
    </div>
  );
}

export default App;